import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import websocketNotificationService from "../../services/WebsocketNotificationService";
import notificationSnackbarService, {
  openOrderModal,
} from "../../services/NotificationSnackbarService";
import { useTheme, useMediaQuery } from "@mui/material";
import { pulseAnimation } from "../../utils/animations";
import api from "../../services/ApiService";

interface NotificationData {
  id: number;
  title: string;
  body: string;
  type: string;
  order_id?: number;
  created_at: string;
  is_read: boolean;
  actionUrl?: string;
}

const NotificationBell: React.FC = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isConnectedRef = useRef(false);
  const previousTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !token) return;

    // Загружаем непрочитанные уведомления с сервера при монтировании
    const loadUnreadCount = async () => {
      try {
        const response = await api
          .getAxiosInstance()
          .get("/notifications/unread");

        // Проверяем разные форматы ответа от сервера
        const data = response.data?.data ?? response.data;
        const count = Array.isArray(data)
          ? data.length
          : (response.data?.count ?? response.data?.unread_count ?? 0);

        setUnreadCount(count);
      } catch (error) {
        console.error("[NotificationBell] Failed to load unread count:", error);
      }
    };

    // Загружаем последние уведомления для отображения в меню
    const loadNotifications = async () => {
      try {
        const response = await api
          .getAxiosInstance()
          .get("/notifications?limit=10");
        const notificationsData = response.data?.data ?? [];

        const formattedNotifications: NotificationData[] =
          notificationsData.map((n: any) => ({
            id: n.id,
            title: n.title || t("notifications.new_notification"),
            body: n.message || "",
            type: n.type || "system",
            order_id: n.data ? JSON.parse(n.data)?.id : undefined,
            created_at: n.created_at,
            is_read: n.read ?? false,
            actionUrl: n.data ? `/orders/${JSON.parse(n.data)?.id}` : undefined,
          }));

        setNotifications(formattedNotifications);
      } catch (error) {
        console.error(
          "[NotificationBell] Failed to load notifications:",
          error,
        );
      }
    };

    loadUnreadCount();
    loadNotifications();

    // WebSocket подключение управляется глобально через AuthContext
    // NotificationBell только слушает события, не создает подключение
    // Подписываемся на события от глобального WebSocket
    websocketNotificationService.onNewNotification((data) => {
      const newNotification: NotificationData = {
        id: data.id || Date.now(),
        title: data.title || t("notifications.new_notification"),
        body: data.body || data.message || "",
        type: data.type || "system",
        order_id: data.order_id,
        created_at: data.created_at || new Date().toISOString(),
        is_read: false,
        actionUrl: data.order_id
          ? `/orders/${data.order_id}` // Формат: /orders/:orderId (соответствует роуту)
          : data.type === "finance"
            ? "/finance"
            : undefined,
      };

      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Показываем Snackbar уведомление
      notificationSnackbarService.show({
        id: newNotification.id,
        type: newNotification.type as any,
        title: newNotification.title,
        message: newNotification.body,
        priority: "normal",
        actionLabel: newNotification.actionUrl ? t("notif_view") : undefined,
        onAction: newNotification.order_id
          ? () => openOrderModal(newNotification.order_id!)
          : newNotification.actionUrl
            ? () => (window.location.href = newNotification.actionUrl!)
            : undefined,
        duration: 6000,
      });
    });

    // Подписка на обновления заказов
    websocketNotificationService.onOrderUpdate((data) => {
      // Данные заказа могут быть в data.order (status_change) или data.data (enhanced_notification)
      const orderData = data.order || data.data;
      const orderId = orderData?.id || data.id;
      const status = orderData?.status || data.status;
      const orderTitle = data.title || t("order_hash", { id: orderId });

      const newNotification: NotificationData = {
        id: Date.now(),
        title: t("notifications.order_update"),
        body: `${orderTitle}: ${status}`,
        type: "order_status_changed",
        order_id: orderId,
        created_at: new Date().toISOString(),
        is_read: false,
        actionUrl: `/orders/${orderId}`,
      };

      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Показываем Snackbar уведомление для обновления заказа
      notificationSnackbarService.showOrderStatusChanged(
        orderId,
        orderTitle,
        status,
      );
    });

    // Обработчик обновления токена
    const handleTokenRefresh = () => {
      // Флаг будет сброшен при следующем эффекте
    };

    // Обработчик разлогинивания - отключаем WebSocket
    const handleLogout = () => {
      websocketNotificationService.manualDisconnect();
      isConnectedRef.current = false;
      previousTokenRef.current = null;
      setNotifications([]);
      setUnreadCount(0);
    };

    window.addEventListener("auth-token-expired", handleTokenRefresh);
    window.addEventListener("auth-logout", handleLogout);

    return () => {
      // НЕ вызываем disconnect() здесь - NotificationBell остается в DOM
      // и должен поддерживать соединение при навигации между страницами
      // Disconnect вызывается только при смене токена или разлогинивании
      window.removeEventListener("auth-token-expired", handleTokenRefresh);
      window.removeEventListener("auth-logout", handleLogout);
    };
  }, [user, token]);

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    // При открытии меню автоматически помечаем все уведомления прочитанными
    if (unreadCount > 0) {
      const currentCount = unreadCount;
      // Сбрасываем счётчик и помечаем локально
      setUnreadCount(0);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true })),
      );

      // Отправляем запрос на сервер
      try {
        await api.getAxiosInstance().put("/notifications/read-all");
      } catch (_) {
        console.error("[NotificationBell] Failed to mark all as read on open");
        // Если запрос не удался, возвращаем счётчик обратно
        setUnreadCount(currentCount);
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, is_read: false })),
        );
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    // Помечаем как прочитанное локально
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    // Отправляем запрос на сервер для пометки как прочитанное
    try {
      await api
        .getAxiosInstance()
        .put(`/notifications/${notification.id}/read`);
    } catch (error) {
      console.error(
        "[NotificationBell] Failed to mark notification as read:",
        error,
      );
    }

    // Открыть модальное окно заказа
    if (notification.order_id) {
      openOrderModal(notification.order_id);
    } else if (notification.actionUrl) {
      const actionUrl = notification.actionUrl;
      if (actionUrl.startsWith("/orders/")) {
        const orderId = parseInt(actionUrl.split("/orders/")[1], 10);
        if (!isNaN(orderId)) {
          openOrderModal(orderId);
          return;
        }
      }
      window.location.href = actionUrl;
    }
    handleClose();
  };

  const handleMarkAllAsRead = async () => {
    setNotifications([]);
    setUnreadCount(0);

    // Отправляем запрос на сервер для удаления всех уведомлений
    try {
      await api.getAxiosInstance().put("/notifications/read-all");
    } catch (error) {
      console.error("[NotificationBell] Failed to clear notifications:", error);
    }

    handleClose();
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Tooltip title={t("notifications") || "Уведомления"}>
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{
            position: "relative",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                animation:
                  unreadCount > 0
                    ? `${pulseAnimation} 2s ease-in-out infinite`
                    : "none",
                background:
                  unreadCount > 0
                    ? "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)"
                    : undefined,
                boxShadow:
                  unreadCount > 0 ? "0 0 12px rgba(239, 68, 68, 0.6)" : "none",
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            minWidth: 300,
            maxWidth: 400,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="h6">Уведомления</Typography>
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Нет уведомлений
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
              {notifications.slice(0, 10).map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    backgroundColor: notification.is_read
                      ? "inherit"
                      : "action.hover",
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: notification.is_read
                          ? "grey.300"
                          : "primary.main",
                        width: 32,
                        height: 32,
                      }}
                    >
                      {notification.type.includes("order")
                        ? "📦"
                        : notification.type.includes("finance")
                          ? "💰"
                          : "ℹ️"}
                    </Avatar>
                  </ListItemIcon>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {notification.body}
                    </Typography>
                  </Box>
                  {!notification.is_read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        ml: 1,
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
            <Divider />
            <MenuItem onClick={handleMarkAllAsRead}>
              <ListItemIcon>
                <DeleteSweepIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Очистить все</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
